import React, { useEffect, useMemo, useRef, useState } from 'react';

enum FormQueryState {
  Loading,
  Success,
  Failed,
}

export interface FormQueryResult {
  data?: any;
  isSuccess: boolean;
  isError: boolean;
  isLoading: boolean;
}

interface FormProps {
  className?: string;
  children?: any;
  onSubmit: Function;
  queryResult: FormQueryResult | FormQueryResult[];
  onQuerySuccess?: Function;
  onQueryFailed?: Function;
}

export const Form = ({
  className,
  children,
  onSubmit,
  queryResult,
  onQuerySuccess,
  onQueryFailed,
}: FormProps) => {
  const [disabled, setDisabled] = useState(false);
  const previousQueryStateRef = useRef<FormQueryState | undefined>(undefined);

  const queryState = useMemo((): FormQueryState | undefined => {
    const queries = Array.isArray(queryResult) ? queryResult : [queryResult as FormQueryResult];
    if (queries.some((q) => q.isLoading)) return FormQueryState.Loading;
    if (queries.some((q) => q.isError)) return FormQueryState.Failed;
    if (queries.every((q) => q.isSuccess)) return FormQueryState.Success;
    return undefined;
  }, [queryResult]);

  useEffect(() => {
    setDisabled(queryState === FormQueryState.Loading);

    // Nothing has changed, don't trigger callbacks
    if (queryState === previousQueryStateRef.current) {
      return;
    }
    previousQueryStateRef.current = queryState;

    if (queryState === FormQueryState.Success) {
      onQuerySuccess?.();
    } else if (queryState === FormQueryState.Failed) {
      onQueryFailed?.();
    }
  }, [queryState, onQuerySuccess, onQueryFailed]);

  const traverseChildren = (nextchildren: any): React.ReactNode => {
    return React.Children.map(nextchildren, (child: any) => {
      if (!React.isValidElement(child)) return child;

      const { children: childChildren, ...otherProps } = child.props as any;
      if (childChildren) {
        return React.cloneElement(child, {
          ...otherProps,
          children: traverseChildren(childChildren),
        });
      }
      const { isSumbit } = child.props as any;

      if (isSumbit) {
        // isSumbit = true means this is submit button, we need to overwrite the onClick and isLoading
        return React.cloneElement(child, {
          ...otherProps,
          disabled: disabled || otherProps.disabled,
          onClick: onSubmit,
          isLoading: queryState === FormQueryState.Loading,
        });
      }
      // TODO: CB To be more accurate, the disable should only override from elements like input, dropdown, switch
      return React.cloneElement(child, {
        ...otherProps,
        disabled: disabled || otherProps.disabled,
      });
    });
  };

  return <div className={className}>{children && traverseChildren(children)}</div>;
};
